import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { BlockScrollStrategy, GlobalPositionStrategy, OverlayRef } from '@angular/cdk/overlay';
import { ApplicationRef } from '@angular/core';
import { App } from '@df/core/app';
import { NgInject } from '@df/ng/ng-inject';
import { ModalPosition } from '@df/ui/modal/modal-config';
import { ModalContainerAbstract } from '@df/ui/modal/modal-container.abstract';
import { Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ModalOverlay } from './modal-overlay';

// Counter for unique modal ids.
let uniqueId = 0;

/** Possible states of the lifecycle of a modal. */
export const enum ModalState {
    OPEN,
    CLOSING,
    CLOSED
}

/**
 * Reference to a modal opened via the Modal service.
 */
export class ModalRef<T, R = any> {
    /** The instance of component opened into the modal. */
    componentInstance!: T;

    /** Whether the user is allowed to close the modal. */
    disableClose: boolean | undefined = this.containerInstance.config.disableClose;

    /** Whether the user is allowed to close the modal. */
    onBackdropClick: (() => void) | undefined = this.containerInstance.config.onBackdropClick;

    /** Subject for notifying the user that the modal has finished opening. */
    protected readonly _afterOpened = new Subject<void>();

    /** Subject for notifying the user that the modal has finished closing. */
    protected readonly _afterClosed = new Subject<R | undefined>();

    /** Subject for notifying the user that the modal has started closing. */
    protected readonly _beforeClosed = new Subject<R | undefined>();

    /** Result to be passed to afterClosed. */
    protected result: R | undefined;

    /** Handle to the timeout that's running as a fallback in case the exit animation doesn't fire. */
    protected closeFallbackTimeout: any;

    /** Current state of the modal. */
    protected state = ModalState.OPEN;

    @NgInject(ModalOverlay)
    protected overlay!: ModalOverlay;

    @NgInject(ApplicationRef)
    protected applicationRef!: ApplicationRef;

    @NgInject(App)
    protected app!: App;

    constructor(
        protected overlayRef: OverlayRef,
        public containerInstance: ModalContainerAbstract,
        readonly id: string = `modal-${uniqueId++}`
    ) {
        // Pass the id along to the container.
        containerInstance.id = id;

        // Emit when opening animation completes
        containerInstance.animationStateChanged
            .pipe(
                filter(event => event.phaseName === 'done' && event.toState === 'enter'),
                take(1)
            )
            .subscribe(() => {
                this._afterOpened.next();
                this._afterOpened.complete();
            });

        // Dispose overlay when closing animation is complete
        containerInstance.animationStateChanged
            .pipe(
                filter(event => event.phaseName === 'done' && event.toState === 'exit'),
                take(1)
            )
            .subscribe(() => {
                clearTimeout(this.closeFallbackTimeout);
                this.overlayRef.dispose();
            });

        overlayRef.detachments().subscribe(() => {
            this._beforeClosed.next(this.result);
            this._beforeClosed.complete();
            this._afterClosed.next(this.result);
            this._afterClosed.complete();
            this.componentInstance = null!;
            this.overlayRef.dispose();
        });

        overlayRef
            .keydownEvents()
            .pipe(
                filter(event => {
                    return event.keyCode === ESCAPE && !this.disableClose && !hasModifierKey(event);
                })
            )
            .subscribe(event => {
                event.preventDefault();
                this.close();
            });
    }

    /**
     * Close the modal.
     * @param modalResult Optional result to return to the modal opener.
     */
    close(modalResult?: R): void {
        this.result = modalResult;

        // Transition the backdrop in parallel to the modal.
        this.containerInstance.animationStateChanged
            .pipe(
                filter(event => event.phaseName === 'start'),
                take(1)
            )
            .subscribe(event => {
                this._beforeClosed.next(modalResult);
                this._beforeClosed.complete();
                this.state = ModalState.CLOSED;
                this.overlayRef.detachBackdrop();
                // removes scroll block instantly on close,
                // fixes issue with keeping scroll position after location change while link was clicked inside modal.
                if (this.overlayRef.getConfig().scrollStrategy instanceof BlockScrollStrategy) {
                    this.overlayRef.updateScrollStrategy(this.overlay.scrollStrategies.noop());
                }
                // The logic that disposes of the overlay depends on the exit animation completing, however
                // it isn't guaranteed if the parent view is destroyed while it's running. Add a fallback
                // timeout which will clean everything up if the animation hasn't fired within the specified
                // amount of time plus 100ms. We don't need to run this outside the NgZone, because for the
                // vast majority of cases the timeout will have been cleared before it has the chance to fire.
                this.closeFallbackTimeout = setTimeout(() => {
                    this.overlayRef.dispose();
                }, event.totalTime + 100);
            });

        this.containerInstance.startExitAnimation();
        this.state = ModalState.CLOSING;

        if (this.app?.ngZoneOff) {
            this.applicationRef.tick();
        }
    }

    /**
     * Gets an observable that is notified when the modal is finished opening.
     */
    afterOpened(): Observable<void> {
        return this._afterOpened.asObservable();
    }

    /**
     * Gets an observable that is notified when the modal is finished closing.
     */
    afterClosed(): Observable<R | undefined> {
        return this._afterClosed.asObservable();
    }

    /**
     * Gets an observable that is notified when the modal has started closing.
     */
    beforeClosed(): Observable<R | undefined> {
        return this._beforeClosed.asObservable();
    }

    /**
     * Gets an observable that emits when the overlay's backdrop has been clicked.
     */
    backdropClick(): Observable<MouseEvent> {
        return this.overlayRef.backdropClick();
    }

    /**
     * Gets an observable that emits when keydown events are targeted on the overlay.
     */
    keydownEvents(): Observable<KeyboardEvent> {
        return this.overlayRef.keydownEvents();
    }

    /**
     * Updates the modal's position.
     * @param position New modal position.
     */
    updatePosition(position?: ModalPosition): this {
        const strategy = this.getPositionStrategy();

        if (position && (position.left || position.right)) {
            position.left ? strategy.left(position.left) : strategy.right(position.right);
        } else {
            strategy.centerHorizontally();
        }

        if (position && (position.top || position.bottom)) {
            position.top ? strategy.top(position.top) : strategy.bottom(position.bottom);
        } else {
            strategy.centerVertically();
        }

        this.overlayRef.updatePosition();

        return this;
    }

    /**
     * Updates the modal's width and height.
     * @param width New width of the modal.
     * @param height New height of the modal.
     */
    updateSize(width: string = '', height: string = ''): this {
        this.getPositionStrategy().width(width).height(height);
        this.overlayRef.updatePosition();
        return this;
    }

    /** Add a CSS class or an array of classes to the overlay pane. */
    addPanelClass(classes: string | string[]): this {
        this.overlayRef.addPanelClass(classes);
        return this;
    }

    /** Remove a CSS class or an array of classes from the overlay pane. */
    removePanelClass(classes: string | string[]): this {
        this.overlayRef.removePanelClass(classes);
        return this;
    }

    /**
     * Gets an observable that is notified when the modal is finished opening.
     * @deprecated Use `afterOpened` instead.
     * @breaking-change 8.0.0
     */
    afterOpen(): Observable<void> {
        return this.afterOpened();
    }

    /**
     * Gets an observable that is notified when the modal has started closing.
     * @deprecated Use `beforeClosed` instead.
     * @breaking-change 8.0.0
     */
    beforeClose(): Observable<R | undefined> {
        return this.beforeClosed();
    }

    /** Gets the current state of the modal's lifecycle. */
    getState(): ModalState {
        return this.state;
    }

    /** Fetches the position strategy object from the overlay ref. */
    protected getPositionStrategy(): GlobalPositionStrategy {
        return this.overlayRef.getConfig().positionStrategy as GlobalPositionStrategy;
    }
}
